import { useState } from "react"
import { Link, usePage } from "@inertiajs/react"
import {
  BookOpen,
  Calendar,
  Clock,
  FileText,
  Globe,
  Headphones,
  Heart,
  Image as ImageIcon,
  List,
  MessageSquare,
  ScrollText,
  Send,
  Sparkles,
  Star,
  Timer,
  User,
  Video,
} from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { messageFreelancer, toggleFavorite } from "@/lib/hireActions"
import { launchVideoCall } from "@/lib/videoCall"
import FreelancerAvatar from "@/components/tuw/hire/FreelancerAvatar"
import StarRating from "@/components/tuw/hire/StarRating"
import TierBadge from "@/components/tuw/hire/TierBadge"
import ReviewCard from "@/components/tuw/hire/ReviewCard"
import FunnelDeferredDialog from "@/components/tuw/hire/FunnelDeferredDialog"
import AccountBox from "@/components/tuw/viewingRoom/AccountBox"
import GuestShell from "@/components/tuw/viewingRoom/GuestShell"
import { HandoffDialog, InviteButton, InviteFailed, useInvite } from "@/components/tuw/viewingRoom/Invitation"
import ResumedNotice, { useResumed } from "@/components/tuw/viewingRoom/ResumedNotice"
import { guestProfile } from "@/lib/viewingRoom/guest"
import { VR, fill } from "@/lib/viewingRoom/wording"

const TAB_ICON = {
  file: FileText,
  image: ImageIcon,
  headphones: Headphones,
  user: User,
  details: ScrollText,
  list: List,
  star: Star,
}

function PanelCard({ icon: Icon, title, sub, children }) {
  return (
    <div className="overflow-hidden rounded-[16px] border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-[22px] py-[16px]">
        <h3 className="flex items-center gap-[9px] font-display text-[14.5px] font-bold">
          {Icon && <Icon className="size-[17px] text-primary" strokeWidth={1.9} />}
          {title}
        </h3>
        {sub && <span className="text-[12px] font-medium text-ink-3">{sub}</span>}
      </div>
      <div className="p-[22px]">{children}</div>
    </div>
  )
}

function EmptyPanel({ text }) {
  return <p className="text-[13.5px] text-ink-3">{text}</p>
}

// Viewing Room: a signed-out visitor gets the public profile (`viewer:
// "guest"`) in the guest frame: the signed-in card's figures and stats, and the
// approved guest actions, each opening the account box
// (AccountBox) for its action; Invite carries a writer or editor into Open House
// and hands any other freelancer off to today's sign-up and service form.
// A customer landing from that box sees its resumed Save (useResumed); a
// resumed sample opens the request form, which still asks for the details and
// today's price, and offers it again until it is sent. A guest's links to
// other profiles load the whole document, so each profile's own title,
// canonical and robots tags arrive (they live in the Rails layout).
export default function FreelancerProfile() {
  const { freelancer: data, auth, viewer, questionLabels, account } = usePage().props
  const guest = viewer === "guest"
  const f = guest ? guestProfile(data, questionLabels) : data
  const [activeTab, setActiveTab] = useState(f.tabs[0]?.key)
  const [fav, setFav] = useState(f.isFavorite)
  const resumed = useResumed()
  const resumedSample = !guest && resumed === "sample" && f.sampleRequest?.eligible === true
  const [funnel, setFunnel] = useState({ open: resumedSample, kind: resumedSample ? "sample" : "invite" })
  const [sampleSent, setSampleSent] = useState(false)
  const [boxFor, setBoxFor] = useState(null)

  const firstName = (f.penName ?? "").split(" ")[0]
  const ProfileLink = guest ? "a" : Link
  const canMessage = auth?.canMessage ?? true
  // Whether this profile may offer a sample request at all, and why not when it
  // can't. Re-derived on the endpoint too — this only shapes the button.
  const sample = f.sampleRequest ?? { offered: false, eligible: false, reason: null }
  // Same shape for extra hours. Open to any role, but only to freelancers who
  // hold the out-of-scope qualification — hence its own `offered`.
  const extraHours = f.outOfScopeRequest ?? { offered: false, eligible: false, reason: null }
  const canVideoCall = auth?.canVideoCall ?? false
  const onMessage = () => messageFreelancer(f.id)
  const onToggleFav = () => {
    setFav((v) => !v)
    toggleFavorite(f.id, { only: ["navCounts"] })
  }

  const card = f.card

  function meta() {
    const rows = []
    if (f.languages?.length) rows.push([Globe, "Speaks", f.languages.join(", ")])
    rows.push([Calendar, "Availability", card.availability?.label || "—"])
    if (card.replyTime) rows.push([Clock, "Avg. reply", card.replyTime])
    if (f.type === "writer" && card.words) rows.push([FileText, "Lifetime words", card.words.toLocaleString()])
    return rows
  }

  function renderPanel() {
    if (activeTab === "portfolio") return renderPortfolio()
    if (activeTab === "overview") return renderOverview()
    if (activeTab === "bio") return renderBio()
    if (activeTab === "niches") return renderNiches()
    if (activeTab === "reviews") return renderReviews()
    return null
  }

  function renderOverview() {
    return (
      <div className="flex flex-col gap-[16px]">
        {f.overview.headline && (
          <div className="rounded-[16px] border border-border bg-accent px-[26px] py-[24px]">
            <div className="mb-[10px] text-[10.5px] font-bold tracking-[0.1em] text-ink-3 uppercase">Headline</div>
            <div className="font-display text-[19px] leading-[1.4] font-semibold tracking-[-0.2px]">{f.overview.headline}</div>
          </div>
        )}
        <PanelCard icon={User} title={`Get to know ${firstName}`}>
          {f.overview.facts.length === 0 ? (
            <EmptyPanel text={`${firstName} hasn't filled in their get-to-know-me answers yet.`} />
          ) : (
            <div className="flex flex-col">
              {f.overview.facts.map((q) => (
                <div key={q.key} className="border-b border-border py-[16px] first:pt-0 last:border-none last:pb-0">
                  <div className="mb-[6px] text-[12.5px] font-bold text-ink-3">{q.label}</div>
                  <div className="text-[14px] leading-[1.6] text-foreground">{q.answer}</div>
                </div>
              ))}
            </div>
          )}
        </PanelCard>
      </div>
    )
  }

  function renderBio() {
    return (
      <PanelCard icon={ScrollText} title="Biography" sub="Professional background">
        {f.bio ? (
          <p className="text-[14px] leading-[1.65] text-muted-foreground">{f.bio}</p>
        ) : (
          <EmptyPanel text={`${firstName} hasn't added a biography yet.`} />
        )}
      </PanelCard>
    )
  }

  function renderNiches() {
    return (
      <PanelCard icon={List} title="Niches & topics" sub={`${f.niches.length} specialties`}>
        {f.niches.length === 0 ? (
          <EmptyPanel text="No niches listed yet." />
        ) : (
          <div className="flex flex-wrap gap-[9px]">
            {f.niches.map((n) => (
              <span key={n.key} className="rounded-[10px] border border-border bg-secondary px-[15px] py-[9px] text-[13px] font-semibold text-foreground">
                {n.label}
              </span>
            ))}
          </div>
        )}
        {f.languages?.length > 0 && (
          <div className="mt-[24px]">
            <div className="mb-[12px] text-[10.5px] font-bold tracking-[0.1em] text-ink-3 uppercase">Languages</div>
            <div className="flex flex-wrap gap-[9px]">
              {f.languages.map((l) => (
                <span key={l} className="flex items-center gap-[8px] rounded-[10px] border border-line-2 px-[15px] py-[9px] text-[13px] font-semibold text-muted-foreground">
                  <Globe className="size-[15px] text-ink-3" strokeWidth={1.9} />
                  {l}
                </span>
              ))}
            </div>
          </div>
        )}
      </PanelCard>
    )
  }

  function renderPortfolio() {
    const { kind, items } = f.portfolio
    if (items.length === 0) {
      const empty = guest ? VR.profile.noPublicSamples : `${firstName} hasn't uploaded any samples yet.`
      return <PanelCard icon={FileText} title="Portfolio"><EmptyPanel text={empty} /></PanelCard>
    }
    if (kind === "snippets") {
      return (
        <PanelCard icon={FileText} title="Content snippets" sub={`Sample writing · ${items.length} ${items.length === 1 ? "piece" : "pieces"}`}>
          <div className="flex flex-col gap-[14px]">
            {items.map((s, i) => (
              <div key={i} className="overflow-hidden rounded-[13px] border border-border">
                <div className="flex items-center justify-between border-b border-border bg-accent px-[18px] py-[14px]">
                  <div className="font-display text-[14px] font-bold">{s.title}</div>
                  {s.niche && <span className="rounded-[7px] bg-secondary px-[9px] py-[3px] text-[11px] font-semibold text-muted-foreground">{s.niche}</span>}
                </div>
                <div className="px-[18px] py-[18px] text-[14px] leading-[1.7] text-muted-foreground italic">"{s.excerpt}"</div>
              </div>
            ))}
          </div>
        </PanelCard>
      )
    }
    if (kind === "gallery") {
      const label = f.type === "designer" ? "Cover portfolio" : "Illustration samples"
      return (
        <PanelCard icon={ImageIcon} title={label} sub={`${items.length} ${items.length === 1 ? "piece" : "pieces"}`}>
          <div className="grid grid-cols-3 gap-[14px]">
            {items.map((g, i) => (
              <div key={i}>
                <div className="overflow-hidden rounded-[11px] border border-line-2 bg-secondary" style={{ aspectRatio: f.type === "designer" ? "2 / 3" : "1 / 1" }}>
                  <img src={g.url} alt={g.title ?? ""} loading="lazy" className="size-full object-cover" />
                </div>
                {g.title && <div className="mt-[9px] text-[12.5px] font-semibold">{g.title}</div>}
              </div>
            ))}
          </div>
        </PanelCard>
      )
    }
    // audio
    return (
      <PanelCard icon={Headphones} title="Narration samples" sub={`${items.length} audio ${items.length === 1 ? "clip" : "clips"}`}>
        <div className="flex flex-col gap-[12px]">
          {items.map((a, i) => (
            <div key={i} className="rounded-[13px] border border-border px-[18px] py-[15px]">
              <div className="font-display text-[13.5px] font-bold">{a.title}</div>
              {a.description && <div className="mt-[3px] text-[12.5px] text-ink-3">{a.description}</div>}
              {a.audioUrl && <audio controls preload="none" src={a.audioUrl} className="mt-[10px] h-[36px] w-full" />}
            </div>
          ))}
        </div>
      </PanelCard>
    )
  }

  function renderReviews() {
    return (
      <PanelCard
        icon={Star}
        title="Reviews"
        sub={card.rating != null ? `${card.rating.toFixed(1)} · ${card.reviews} customer reviews` : guest ? VR.profile.noReviews : "No reviews yet"}
      >
        {f.reviews.length === 0 ? (
          <EmptyPanel text={guest ? fill(VR.profile.noReviewsBody, { pen_name: f.penName }) : `${firstName} doesn't have any customer reviews yet.`} />
        ) : (
          <div>{f.reviews.map((r, i) => <ReviewCard key={i} review={r} />)}</div>
        )}
      </PanelCard>
    )
  }

  return (
    <>
      {!guest && resumed === "save" && fav && <ResumedNotice>{fill(VR.resumed.saved, { pen_name: f.penName })}</ResumedNotice>}
      {resumedSample && !sampleSent && !(funnel.open && funnel.kind === "sample") && (
        <ResumedNotice>
          <span className="flex flex-wrap items-center justify-between gap-[10px]">
            <span>{VR.resumed.sampleNotSent}</span>
            <Button size="sm" onClick={() => setFunnel({ open: true, kind: "sample" })}>{VR.resumed.sampleContinue}</Button>
          </span>
        </ResumedNotice>
      )}
      {guest && (
        <AccountBox account={account} onClose={() => setBoxFor(null)}
          request={boxFor && { action: boxFor, slug: f.slug, penName: f.penName }} />
      )}
      {/* `.pwrap` + `.pcard`, design/spec/shared/freelancers.css :262/:263, and the
          ONE responsive block that governs them, :298 —
            @media (max-width:860px){
              .pwrap{grid-template-columns:1fr;gap:16px;padding:16px 14px 40px;}
              .pcard{position:static;}
            }
          Tailwind v4 compiles `max-[N]` as `(width < N)`, so the ruled ≤860 edge
          is written 861. THE DEFECT this closes: the 312px track is FIXED, so
          below ~360px of content box the right column resolved to zero width and
          its children rendered off-screen — measured on the base commit, `main`
          overflowed by 215px at 365, 160 at 420 and 20/19 at 560/561, carrying
          the whole tab rail + panel + similar rail past the right edge.
          The bottom padding stays on the page's own `py` convention rather than
          the design's 40px: desktop already ports `24px 26px 48px` as
          `px-[26px] py-[24px]`, and that divergence is out of this slice. */}
      <div className="grid grid-cols-[312px_1fr] items-start gap-[22px] px-[26px] py-[24px] max-[861px]:grid-cols-1 max-[861px]:gap-[16px] max-[861px]:px-[14px] max-[861px]:py-[16px]">
        {/* LEFT — profile card */}
        {/* A guest's frame has its own sticky header (GuestShell), so the card
            sticks below it. */}
        <aside className={cn("sticky overflow-hidden rounded-[16px] border border-border bg-card max-[861px]:static", guest ? "top-[82px]" : "top-[18px]")}>
          <div className="p-[24px_22px_22px]">
            <div className="mb-[13px]"><FreelancerAvatar name={f.penName} src={f.avatarUrl} size={88} online={card.online} /></div>
            <div className="flex flex-wrap items-center gap-[9px] font-display text-[21px] font-bold tracking-[-0.3px]">{f.penName}</div>
            <div className="mt-[3px] flex flex-wrap items-center gap-[6px] text-[13.5px] text-muted-foreground">
              {f.role}
              {(f.specialties ?? []).map((specialty) => (
                <span key={specialty} className="rounded-full bg-secondary px-[8px] py-[2px] text-[10.5px] font-bold text-foreground">
                  {specialty}
                </span>
              ))}
            </div>

            <div className="mt-[11px] flex flex-wrap items-center gap-[8px]">
              <TierBadge tier={f.tier} />
              <span className={cn("inline-flex items-center gap-[5px] text-[11px] font-bold", card.online ? "text-done" : "text-ink-3")}>
                <span className={cn("size-[7px] rounded-full", card.online ? "bg-done" : "bg-ink-3")} />
                {card.online ? "Online now" : card.lastOnline ? `Active ${card.lastOnline}` : "Offline"}
              </span>
            </div>

            <div className="mt-[14px] flex items-center gap-[9px] border-t border-border pt-[14px]">
              <StarRating rating={card.rating} count={card.reviews} showCount={false} />
              <span className="text-[12px] text-ink-3">{card.reviews} customer reviews</span>
            </div>

            <div className="mt-[16px] grid grid-cols-2 gap-px overflow-hidden rounded-[12px] border border-border bg-border">
              {[
                [card.reliability != null ? `${card.reliability}%` : "—", "Reliability"],
                f.tier ? [f.tier, "Tier"] : null,
                [card.samples || "—", "Samples"],
                [card.activeOrders === 0 ? "Open" : card.activeOrders, card.activeOrders === 0 ? "For work" : "Active orders"],
              ].filter(Boolean).map(([v, k], i) => (
                <div key={i} className="bg-card px-[14px] py-[13px]">
                  <div className="font-display text-[16px] font-bold">{v}</div>
                  <div className="mt-[2px] text-[10.5px] font-semibold tracking-[0.03em] text-ink-3 uppercase">{k}</div>
                </div>
              ))}
            </div>

            <div className="mt-[16px] flex flex-col gap-[11px]">
              {meta().map(([Icon, k, v], i) => (
                <div key={i} className="flex items-center gap-[10px] text-[13px] text-muted-foreground">
                  <Icon className="size-4 shrink-0 text-ink-3" strokeWidth={1.9} />
                  <span>{k} · <b className="font-semibold text-foreground">{v}</b></span>
                </div>
              ))}
            </div>

            {guest ? <GuestActions freelancer={f} onAction={setBoxFor} /> : <div className="mt-[18px] flex flex-col gap-[9px]">
              <Button className="justify-center" onClick={() => setFunnel({ open: true, kind: "invite" })}>
                <Send className="size-4" strokeWidth={2} />
                Invite
              </Button>
              {/* `.pcard .pc-act .row2`, freelancers.css :282/:283, and its ≤560
                  rule at :302 — `{flex-wrap:wrap}` with `.btn{flex:1 1 40%}`.
                  `flex-1` is `1 1 0%`, so both buttons stay on one crushed line;
                  a 40% basis makes the second one wrap onto its own full line
                  once the card is the whole phone width. */}
              <div className="flex gap-[9px] max-[561px]:flex-wrap">
                <Button
                  variant="outline"
                  className="flex-1 justify-center max-[561px]:flex-[1_1_40%]"
                  onClick={onMessage}
                  disabled={!canMessage}
                  title={canMessage ? undefined : "Direct messaging is unavailable on your account"}
                >
                  <MessageSquare className="size-4" strokeWidth={2} />
                  Message
                </Button>
                {/* Writers only (UPDA-74). The legacy profile card offered this
                    for every freelancer and rejected non-writers after the whole
                    form was filled in; the server decides here, and the button is
                    simply absent where a sample can't be asked for. */}
                {sample.offered && (
                  <Button
                    variant="outline"
                    className="flex-1 justify-center max-[561px]:flex-[1_1_40%]"
                    onClick={() => setFunnel({ open: true, kind: "sample" })}
                    disabled={!sample.eligible}
                    title={sample.eligible ? undefined : sample.reason}
                  >
                    <BookOpen className="size-4" strokeWidth={2} />
                    Request sample
                  </Button>
                )}
              </div>
              {/* Its own full-width row rather than a third button beside
                  Message: at the 312px card width the two-up row is already
                  tight, and the aside is `overflow-hidden`, so a third would
                  clip silently instead of wrapping. */}
              {extraHours.offered && (
                <Button
                  variant="outline"
                  className="justify-center"
                  onClick={() => setFunnel({ open: true, kind: "outOfScope" })}
                  disabled={!extraHours.eligible}
                  title={extraHours.eligible ? undefined : extraHours.reason}
                >
                  <Timer className="size-4" strokeWidth={2} />
                  Request extra hours
                </Button>
              )}
              {canVideoCall && f.videoUserId && (
                <Button variant="outline" className="justify-center" onClick={() => launchVideoCall(f.videoUserId)} title="Video call">
                  <Video className="size-4" strokeWidth={2} />
                  Video call
                </Button>
              )}
              {sample.offered && !sample.eligible && sample.reason && (
                <p className="text-[11.5px] leading-[1.45] text-ink-3">{sample.reason}</p>
              )}
              {extraHours.offered && !extraHours.eligible && extraHours.reason && (
                <p className="text-[11.5px] leading-[1.45] text-ink-3">{extraHours.reason}</p>
              )}
              {!canMessage && (
                <p className="text-[11.5px] leading-[1.45] text-ink-3">
                  Direct messaging isn't available on your account right now.
                </p>
              )}
              <Button
                variant={fav ? "outline" : "ghost"}
                className={cn("justify-center", fav && "border-primary/35 bg-[var(--red-soft)] text-primary hover:bg-[var(--red-soft)]")}
                onClick={onToggleFav}
              >
                <Heart className={cn("size-4", fav && "fill-current")} strokeWidth={2} />
                {fav ? "Saved to favorites" : "Save to favorites"}
              </Button>
            </div>}
          </div>
        </aside>

        {/* RIGHT — tabs + panel + similar */}
        <div className="flex min-w-0 flex-col gap-[18px]">
          {/* `.ptabs` (freelancers.css :292) is `overflow-x:auto` with
              `white-space:nowrap` tabs, and this rail already ports both — the
              rail is an INTENTIONAL horizontal scroller, enumerated by
              78-muster-gate.spec.ts, which is why it needs a stable handle. */}
          <div data-testid="profile-tab-rail" className="flex gap-[4px] overflow-x-auto rounded-[13px] border border-border bg-card p-[5px]">
            {f.tabs.map((t) => {
              const Icon = TAB_ICON[t.icon] ?? User
              const on = t.key === activeTab
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={cn(
                    "flex items-center gap-[8px] rounded-[9px] px-[15px] py-[9px] text-[13px] font-semibold whitespace-nowrap transition-colors",
                    on ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <Icon className="size-4" strokeWidth={1.9} />
                  {t.label}
                  {t.count != null && t.count > 0 && (
                    <span className={cn("rounded-full px-[7px] py-px text-[10.5px] font-bold", on ? "bg-white/20 text-background" : "bg-secondary text-muted-foreground")}>
                      {t.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {renderPanel()}

          {f.similar.length > 0 && (
            <div className="mt-[8px]">
              <div className="mb-[13px] flex items-center gap-[9px] font-display text-[14px] font-bold">
                <Sparkles className="size-[17px] text-primary" strokeWidth={1.9} />
                Similar freelancers
              </div>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-[13px]">
                {f.similar.map((s) => (
                  <ProfileLink key={s.id} href={`/freelancers/${s.id}`} className="rounded-[13px] border border-border bg-card p-[15px] transition-colors hover:border-line-2">
                    <div className="flex items-center gap-[11px]">
                      <FreelancerAvatar name={s.penName} src={s.avatarUrl} size={42} online={s.online} />
                      <div className="min-w-0">
                        <div className="truncate font-display text-[14px] font-bold">{s.penName}</div>
                        <div className="text-[11.5px] text-ink-3">{s.role}{s.tier ? ` · ${s.tier}` : ""}</div>
                      </div>
                    </div>
                    <div className="mt-[11px]"><StarRating rating={s.rating} count={s.reviews} /></div>
                  </ProfileLink>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {!guest && <FunnelDeferredDialog
        open={funnel.open}
        onOpenChange={(open) => setFunnel((s) => ({ ...s, open }))}
        freelancer={f}
        kind={funnel.kind}
        notice={resumedSample && !sampleSent && funnel.kind === "sample" ? fill(VR.resumed.sample, { pen_name: f.penName }) : undefined}
        onSent={() => setSampleSent(true)}
      />}
    </>
  )
}

// The guest actions, in the customer card's layout. Invite opens Open House
// with this freelancer, or its handoff; the others open the account box for
// their action (onAction). Extra hours needs an account, so it is not offered.
function GuestActions({ freelancer, onAction }) {
  const half = "flex-1 justify-center max-[561px]:flex-[1_1_40%]"
  const writer = freelancer.type === "writer"
  const invite = useInvite()
  return (
    <div className="mt-[18px] flex flex-col gap-[9px]">
      <InviteButton className="justify-center" freelancer={freelancer} invite={invite} />
      <InviteFailed invite={invite} freelancer={freelancer} />
      <HandoffDialog invite={invite} onProfile />
      <div className="flex gap-[9px] max-[561px]:flex-wrap">
        <Button variant="outline" className={half} onClick={() => onAction("message")}>
          <MessageSquare className="size-4" strokeWidth={2} />
          {VR.action.message}
        </Button>
        {writer && (
          <Button variant="outline" className={half} onClick={() => onAction("sample")}>
            <BookOpen className="size-4" strokeWidth={2} />
            {VR.action.sample}
          </Button>
        )}
      </div>
      <Button variant="outline" className="justify-center" onClick={() => onAction("video")}>
        <Video className="size-4" strokeWidth={2} />
        {VR.action.video}
      </Button>
      <Button variant="ghost" className="justify-center" onClick={() => onAction("save")}>
        <Heart className="size-4" strokeWidth={2} />
        {VR.action.save}
      </Button>
    </div>
  )
}

FreelancerProfile.layout = (page) => {
  // See OrderDetail: Inertia first calls the resolver with the raw props object,
  // then with the page element, so read the freelancer from whichever arrives.
  const props = page.props ?? page
  if (props.viewer === "guest") return <GuestShell>{page}</GuestShell>
  return (
    <AppShell
      active="Find Freelancers"
      crumb={["Hire", "Find Freelancers", props.freelancer?.penName]}
    >
      {page}
    </AppShell>
  )
}
